<script setup>
import Vue, {ref, watch, nextTick, reactive, computed} from "vue";
import {useStripe} from "@/composables/useStripe";
import subscriptionsService from "@/services/subscriptions.service";
import Button from "@/components/ui/Button.vue";
import userService from "@/services/user.service";
import {useStore} from "vue2-helpers/vuex";
import {validEmail} from "@/helpers/string";

const store = useStore();

const emit = defineEmits(['closed']);
const showModal = ref(false);
const showSuccessModal = ref(false);
const loading = ref(false);
const sendingInvoice = ref(false);
const type = ref('card');
const invoiceSent = ref(false);

const form = reactive({
    plan: 'quarterly',
    cardholderName: '',
    zipCode: '',
});

const invoiceForm = reactive({
    name: '',
    title: '',
    email: '',
});

const cardError = ref('');

const prices = {
    quarterly: 3750,
    annually: 12000,
};

let stripe = null;
let cardNumber = null;
let cardExpiry = null;
let cardCvc = null;

const canSendInvoice = computed(() => {
    return invoiceForm.email.length && validEmail(invoiceForm.email) && invoiceForm.name.length && invoiceForm.title.length;
});

watch(showModal, async value => {
    if (!value) {
        if (!invoiceSent.value) {
            emit("closed");
        }

        return;
    }

    await nextTick();

    stripe = await useStripe();
    const elements = stripe.elements();

    const style = {
        base: {
            fontSize: '16px',
            '::placeholder': {
                color: '#aaa',
            }
        }
    };

    cardNumber = elements.create('cardNumber', { style, placeholder: '' });
    cardNumber.mount('#stripe-card-number');

    cardExpiry = elements.create('cardExpiry', { style });
    cardExpiry.mount('#stripe-card-expiry');

    cardCvc = elements.create('cardCvc', { style, placeholder: '' });
    cardCvc.mount('#stripe-card-cvc');
})

async function submit() {
    loading.value = true;
    cardError.value = '';

    const { token, error } = await stripe.createToken(cardNumber, {
        name: form.cardholderName,
        address_zip: form.zipCode,
    });

    if (error) {
        loading.value = false;
        cardError.value = error.message;
        return;
    }

    let postData = {
        ...form,
        token: token.id,
    }

    const {data} = await subscriptionsService.createSubscriptionNew(postData);

    if (Array.isArray(data)) {
        cardError.value = data[0];
    } else {
        const {data: userData} = await userService.getCurrentUserData();
        store.commit('setUser', userData);

        if (store.getters.isPremiumEcosystem) {
            // Vue.notify({
            //     group: 'app',
            //     type: 'success',
            //     duration: 5000,
            //     text: 'Subscription created'
            // })

            showModal.value = false;
            showSuccessModal.value = true;
        }
    }

    loading.value = false;
}

async function sendInvoice() {
    sendingInvoice.value = true;
    const {data} = await userService.sendInvoice(invoiceForm);

    sendingInvoice.value = false;

    if (data) {
        Vue.notify({
            group: 'app',
            type: 'success',
            duration: 5000,
            text: 'Invoice request sent'
        })
    }

    showModal.value = false;
    invoiceSent.value = true;

}

function open() {
    type.value = 'card';
    invoiceForm.name = store.state.user.name;
    invoiceForm.title = '';
    invoiceForm.email = store.state.user.email;

    invoiceSent.value = false;
    showModal.value = true;
}

defineExpose({
    open
});
</script>

<template>
    <div>
        <Modal
            v-model="showModal"
            hide-footer
            hide-header
            :modal-class="['modal-upsell-payment', {'modal-upsell-payment__invoice-modal': type === 'invoice'}]"
        >
            <form v-show="type === 'card'" class="form-v2">
                <div class="modal-upsell-payment__card">
                    <h4 class="heading4 heading5--secondary">Upgrade to SwitchPitch Pro</h4>

                    <p class="modal-upsell-payment__invoice">Need a payment request? <a @click="type = 'invoice'" class="link">Send Invoice Instead</a></p>

                    <fieldset>
                        <legend>card details</legend>

                        <div class="form-v2__item">
                            <label>Cardholder Name</label>
                            <input type="text" v-model="form.cardholderName" />
                        </div>

                        <div class="form-v2__item">
                            <label>Card Number</label>
                            <div id="stripe-card-number"></div>
                        </div>

                        <div class="form-v2__item-cols">
                            <div class="form-v2__item">
                                <label>Exp Date</label>
                                <div id="stripe-card-expiry"></div>
                            </div>
                            <div class="form-v2__item">
                                <label>CVV</label>
                                <div id="stripe-card-cvc"></div>
                            </div>
                        </div>

                        <div class="form-v2__item">
                            <label>Zip Code</label>
                            <input type="text" v-model="form.zipCode" />
                        </div>

                        <div v-if="cardError" style="color: red">{{ cardError }}</div>
                    </fieldset>

                    <div class="modal-upsell-payment-note">Secure payment by Stripe</div>
                </div>

                <fieldset class="modal-upsell-payment__plan">
                    <header class="modal-header">
                        <h6 class="heading6">Your Plan</h6>
                        <button type="button" @click="showModal = false" class="close"></button>
                    </header>

                    <h5 class="heading5">SwitchPitch Pro</h5>

                    <div
                        class="plan-option"
                        :class="{'selected': form.plan === 'quarterly'}"
                        @click="form.plan = 'quarterly'"
                    >
                        <div class="radio" :class="{'checked': form.plan === 'quarterly'}" id="r-quarterly"></div>
                        <div class="plan-info">
                            <div class="plan-name">Quarterly</div>
                            <p>Billed every 3 months</p>
                        </div>
                        <div class="plan-price">$3,750</div>
                    </div>

                    <div
                        class="plan-option"
                        :class="{'selected': form.plan === 'annually'}"
                        @click="form.plan = 'annually'"
                    >
                        <div class="radio" :class="{'checked': form.plan === 'annually'}" id="r-annually"></div>
                        <div class="plan-info">
                            <div class="plan-name">Annual <span class="save-badge">Save 20%</span></div>
                            <p>Billed once per year</p>
                        </div>
                        <div class="plan-price">$12,000</div>
                    </div>

                    <div class="plan-total-row"><span>Total</span><span>${{prices[form.plan].toLocaleString()}}</span></div>

                    <Button
                        class="ui-button w-100"
                        :loading="loading"
                        @click="submit"
                    >
                        Submit Payment
                        <template #loading>
                            Processing...
                        </template>
                    </Button>
                </fieldset>
            </form>

            <form v-if="type === 'invoice'" class="form-v2" :class="{'class': type === 'invoice'}">
                <header class="modal-header">
                    <button class="close"></button>
                </header>

                <h2 class="heading2">Pay Via Invoice</h2>

                <p class="modal-upsell-payment__invoice">Want to pay now? <a @click="type = 'card'" class="link">Pay With A Card</a></p>

                <fieldset>
                    <div class="form-v2__item">
                        <label>Billing Name*</label>
                        <input type="text" v-model="invoiceForm.name" />
                    </div>

                    <div class="form-v2__item">
                        <label>Title*</label>
                        <input type="text" v-model="invoiceForm.title" />
                    </div>

                    <div class="form-v2__item">
                        <label>Email*</label>
                        <input type="text" v-model="invoiceForm.email" />
                    </div>
                </fieldset>

                <div class="modal-footer">
                    <Button
                        :loading="sendingInvoice"
                        :disabled="!canSendInvoice"
                        class="ui-button"
                        @click="sendInvoice"
                    >
                        Send Invoice
                        <template #loading>
                            Sending...
                        </template>
                    </Button>
                </div>
            </form>
        </Modal>

        <Modal v-model="showSuccessModal" hide-footer hide-header modal-class="modal-upsell-payment-success">
            <header class="modal-header">
                <button class="close" @click="showSuccessModal = false"/>
            </header>
            <div class="modal-upsell-payment-success__title">
                <div class="modal-upsell-payment-success__title__inner">
                    <h2 class="heading2">You’ve Gone Pro!</h2>
                    <p>Your receipt was sent to {{store.state.user.email}}</p>
                </div>
            </div>

            <h6 class="heading6">What's Next:</h6>

            <ul class="modal-upsell-payment__list">
                <li>Submit unlimited startups, check statuses and more</li>
                <li>You'll receive your agreement within 1 day</li>
                <li>Optional: Schedule a 15-min setup call</li>
            </ul>

            <footer class="modal-footer">
                <a href="https://meetings.hubspot.com/marigona-xhemaili" target="_blank" class="ui-button ui-button--light">Schedule Onboarding Call</a>
                <a href="#" class="ui-button ui-button--white" @click.prevent="showSuccessModal = false">Close</a>
            </footer>
        </Modal>
    </div>
</template>

<style scoped>
#stripe-card-number,
#stripe-card-expiry,
#stripe-card-cvc {
    padding: 8px 12px;
    width: 100%;
    height: 38px;
    background: #fff;
    border: 1px solid #ddd;
    border-radius: 8px;
}
</style>
